import React, { useState } from 'react';
import { Button } from './ui/button';
import { Shield, Mail, Lock, ArrowRight, Loader2 } from 'lucide-react';

const Login = ({ onLoginSuccess, onBack }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Determine API URL based on environment
            // In production (Vercel), we want relative paths to use the rewrites defined in vercel.json
            // In development, we use the environment variable or fallback to localhost
            const defaultUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';
            const API_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : defaultUrl;

            console.log('Login attempting to connect to:', `${API_URL}/api/login`);

            const response = await fetch(`${API_URL}/api/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });

            const data = await response.json();

            if (response.ok) {
                // As per memory requirements
                localStorage.setItem('token', data.access_token);
                localStorage.setItem('user', JSON.stringify(data.user));
                onLoginSuccess(data.user);
            } else {
                setError(data.detail || 'Login failed. Please check your credentials.');
            }
        } catch (err) {
            setError('Connection error. Is the backend running?');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F6F6F6' }}>
            <div className="w-full max-w-md bg-white rounded-3xl shadow-xl overflow-hidden border border-[#D6E4F0]">
                <div className="p-8">
                    <div className="flex justify-center mb-6">
                        <div className="w-20 h-20 rounded-full overflow-hidden shadow-lg border-2 border-[#D6E4F0] bg-white flex items-center justify-center">
                            <img src="/logo.png" alt="Sage Logo" className="w-full h-full object-cover" />
                        </div>
                    </div>

                    <div className="text-center mb-10 space-y-2">
                        <h2 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Merriweather, serif', color: '#163172' }}>
                            Sage and Seed
                        </h2>
                        <div className="flex justify-center">
                            <span className="text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1 rounded-lg text-white shadow-md inline-block" style={{ background: '#163172' }}>
                                Admin Console
                            </span>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-sm font-semibold ml-1" style={{ color: '#163172' }}>Email Address</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#5a6c7d' }} />
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#D6E4F0] focus:ring-2 focus:ring-[#1E56A0] focus:border-transparent outline-none transition-all"
                                    placeholder="admin@helpingbuddy.com"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-semibold ml-1" style={{ color: '#163172' }}>Password</label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#5a6c7d' }} />
                                <input
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#D6E4F0] focus:ring-2 focus:ring-[#1E56A0] focus:border-transparent outline-none transition-all"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="p-3 rounded-lg text-sm font-medium border border-red-100 bg-red-50 text-red-600">
                                {error}
                            </div>
                        )}

                        <Button
                            disabled={loading}
                            className="w-full py-6 rounded-xl font-bold text-lg flex items-center justify-center group"
                            style={{ background: 'linear-gradient(135deg, #1E56A0 0%, #163172 100%)', color: '#FFFFFF' }}
                        >
                            {loading ? (
                                <Loader2 className="w-6 h-6 animate-spin" />
                            ) : (
                                <>
                                    Sign In <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </Button>
                    </form>
                </div>

                <div className="p-6 text-center border-t border-[#D6E4F0]" style={{ background: '#F8FAFC' }}>
                    <button
                        onClick={onBack}
                        className="text-sm font-medium hover:underline transition-all"
                        style={{ color: '#1E56A0' }}
                    >
                        ← Back to Home
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Login;
